import type {Segmentation} from 'common/types/reports/query';
import type {Segfilter} from 'common/widgets/property-filter-menu/models/segfilter';

// TODO: @opslead - fold these types and possibly other duplicates in this file into iron/common/report/constants.ts
export enum UserRole {
  Admin = `admin`,
  Analyst = `analyst`,
  BillingAdmin = `billing admin`,
  Consumer = `consumer`,
  /** only used as org role, not as a project role */
  Member = `member`,
  Messenger = `messenger`,
  Owner = `owner`,
}

export type OrgUserRole =
  | UserRole.Admin
  | UserRole.Analyst
  | UserRole.BillingAdmin
  | UserRole.Consumer
  | UserRole.Member
  | UserRole.Messenger
  | UserRole.Owner;

export enum EntityType {
  AgentFlow = `agent flow`,
  Behavior = `behavior`,
  Cohort = `cohort`,
  CustomEvent = `customevent`,
  CustomProperty = `custom property`,
  Dashboard = `dashboard`,
  DataGroup = `data group`,
  Event = `event`,
  Experiment = `experiment`,
  FeatureFlag = `feature flag`,
  HeatMap = `heat map`,
  HeatMapCollection = `heat map collection`,
  Journey = `journey`,
  LookupTable = `lookup table`,
  Metric = `metric`,
  MetricTree = `metric-trees`,
  Property = `property`,
  Report = `report`,
  Workspace = `workspace`,
  Playlist = `playlist`,
}

/**
 * These are specialization of EntityType so that they can be specially handled. They will be saved/shared based
 * on the EntityType they are associated with.
 */
export enum SubEntityType {
  // a special type of custom property
  BorrowedProperty = `borrowed property`,
}

/* eslint-disable @typescript-eslint/naming-convention */
/** @lintignore members are accessed dynamically via EntityTypeKey[entityType] */
export enum EntityTypeKey {
  'agent flow' = `AgentFlow`,
  'custom property' = `CustomProperty`,
  'data group' = `DataGroup`,
  'lookup table' = `LookupTable`,
  behavior = `Behavior`,
  cohort = `Cohort`,
  customevent = `CustomEvent`,
  dashboard = `Dashboard`,
  event = `Event`,
  journey = `Journey`,
  metric = `Metric`,
  property = `property`,
  report = `Report`,
  workspace = `Workspace`,
}
/* eslint-enable */

// labels for displaying entity type in copy (non-plural)
export enum EntityTypeNameSingular {
  Cohort = `cohort`,
  CustomEvent = `custom event`,
  CustomProperty = `custom property`,
  Event = `event`,
  EventProperty = `event property`,
  UserProperty = `user property`,
}

/** @lintignore TODO: members accessed dynamically via EntityTypeNamePlural[EntityTypeKey[entityType]]; refactor to a const record so knip can detect usage */
export enum EntityTypeNamePlural {
  AgentFlow = `automations`,
  Behavior = `behaviors`,
  BorrowedProperty = `borrowed properties`,
  Cohort = `cohorts`,
  CustomEvent = `custom events`,
  CustomProperty = `custom properties`,
  Dashboard = `boards`,
  DataGroup = `data groups`,
  Event = `events`,
  Journey = `journeys`,
  LookupTable = `lookup tables`,
  Metric = `formulas`,
  Property = `properties`,
  Report = `reports`,
  Workspace = `data views`,
}

export enum SharableEntityType {
  AgentFlow = `agent-flows`,
  AgentIntelligenceConversations = `agent-intelligence-conversations`,
  Behavior = `behavior`,
  Canvas = `metric-trees`,
  Cohort = `cohorts`,
  CustomEvent = `custom-events`,
  CustomProperty = `custom-properties`,
  Dashboard = `dashboards`,
  Experiment = `experiments`,
  FeatureFlag = `feature-flags`,
  HeatMap = `heat-maps`,
  HeatMapCollection = `heat-map-collections`,
  LookupTable = `lookup-tables`,
  Metric = `metric`,
  // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values -- intentional duplicate
  MetricTree = `metric-trees`,
  Formula = `formula`,
  Profile = `profile`,
  Report = `bookmarks`,
  Theme = `theme`,
  Playlist = `playlists`,
}

export interface SharableEntityGrant {
  owner?: UserShareGrant;
  userShares: UserShareGrant[];
  teamShares: TeamShareGrant[];
  projectShares: ProjectShareGrant[];
  name: string;
  link: string;
}

export enum GrantStatus {
  New = `new`,
  Updated = `updated`,
  Existing = `existing`,
}

export type ProjectShareGrant = {id: number; canEdit: boolean};
export type TeamShareGrant = {id: number; name: string; canEdit: boolean};
export type UserShareGrant = {
  id: number;
  email: string;
  name?: string;
  canEdit: boolean;
  customDefinitionPermissions?: string[];
};

export enum OverrideModal {
  Copy = `copy`,
  Save = `save`,
  Closed = `closed`,
  Delete = `delete`,
  SubsetOfItems = `subset`,
}

export interface EntityTypePermissions {
  role: UserRole;
  can_update_basic: boolean;
  can_share: boolean;
}

export const DEFAULT_NEW_ENTITY_ID = `new`;

export type BaseEntityId = number | string;

interface BaseEntity {
  id?: BaseEntityId;
  description?: string | null;
  can_view?: boolean;
  can_update_basic?: boolean;
  can_share?: boolean;
  is_superadmin?: boolean; // is the user an owner with the ability to override restricted reports for editing and sharing
  allow_staff_override?: boolean; // is the user a staff user without project permissions
}

export interface Entity extends BaseEntity {
  name: string;

  created?: string;
  userId?: number | null;
  userName?: string | null;
  userEmail?: string | null;

  modified?: string;
  lastModifiedUserId?: number;
  lastModifiedUserName?: string;
  lastModifiedUserEmail?: string;
}
export type DashboardCardId = string | number;

export interface PublicDashboard extends BaseEntity {
  id: number;
  title: string;
  description: null | string;
  card_order: number[];
  // cards are not required when getting list of public dashboards
  cards?: Obj[];
  time_filter: Obj;
  filters: Obj[];
  last_modified?: any;

  creator_id: null | number; // null creatorId means it was a legacy auto-generated dashboard with no human creator
  creator_name: null | string; // full name of creator
}

/** alternatives is the list of events that are in a custom event */
export interface CustomEventAlternative {
  event: string;
  serialized?: string;
  valid_segfilter?: ValidSegfilter;
}

export interface ValidSegfilter {
  filters: Segfilter[] | Segmentation[];
  op: string;
}

export interface CustomEventDefinition extends BaseEntity {
  alternatives: CustomEventAlternative[];
  deleted: number;
  id: number;
  isCollectEverything?: boolean;
  is_collect_everything_event: boolean;
  name: string;
  project_id: number;
  value?: string;
}

export const enum UserAccess {
  // eslint-disable-next-line @typescript-eslint/no-shadow
  Creator = `creator`,
  Editor = `editor`,
  Viewer = `viewer`,
  None = `none`,
}

// re-export so entity types are collected under saved-entities/types
export type {CustomProperty} from 'common/types/reports/query';

export const GlobalAccessParamName = `global_access_type`;
export const enum OwnerFilters {
  All = `Created By Anyone`,
  Yours = `Created By You`,
  NotYours = `Not Created By You`,
  Shared = `Shared With You`,
}

export const ENTITY_TYPE_WITH_CONTENT_SUPPORT = [
  EntityType.AgentFlow,
  EntityType.Dashboard,
  EntityType.Experiment,
  EntityType.FeatureFlag,
  EntityType.MetricTree,
  EntityType.Report,
  EntityType.HeatMap,
  EntityType.HeatMapCollection,
  EntityType.Playlist,
] as const;

export type EntityTypeWithContentSupport = (typeof ENTITY_TYPE_WITH_CONTENT_SUPPORT)[number];
